import { useState, useEffect, useCallback, memo } from "react";
import { FiSearch, FiX } from "react-icons/fi";
import api from "../api/api";
import type { Category, Product } from "../types/types";
import { useNavigate, useLocation } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";
import { motion, AnimatePresence } from "framer-motion";
import debounce from "lodash/debounce";
import { useTranslation } from "react-i18next";

// --- API so‘rov ---
export const getSearchResults = async (q: string) => {
  try {
    const res = await api.get(`/products/search/?q=${encodeURIComponent(q)}`);
    return res.data;
  } catch (error) {
    console.error("Search API xatosi:", error);
    return { products: [], categories: [], stores: [] };
  }
};

const SearchInput = () => {
  const [searchValue, setSearchValue] = useState("");
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [stores, setStores] = useState<any[]>([]);
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const lang = useLanguage();
  const { t } = useTranslation();

  // --- Close dropdown when route changes ---
  useEffect(() => {
    setIsDropdownOpen(false);
  }, [location.pathname, location.search]);

  // --- LocalStorage’dan so‘nggi qidiruvlarni olish ---
  useEffect(() => {
    const saved = localStorage.getItem("recentSearches");
    if (saved) setRecentSearches(JSON.parse(saved));
  }, []);

  // --- Qidiruv natijalarini olish (debounce bilan) ---
  const fetchSearchResults = useCallback(
    debounce(async (query: string) => {
      if (!query.trim() || query.trim().length < 2) {
        setProducts([]);
        setCategories([]);
        setStores([]);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      const data = await getSearchResults(query);
      setProducts(data.products || []);
      setCategories(data.categories || []);
      setStores(data.stores || []);
      setIsLoading(false);
    }, 400),
    []
  );

  useEffect(() => {
    fetchSearchResults(searchValue);
    return () => fetchSearchResults.cancel();
  }, [searchValue, fetchSearchResults]);

  const handleSearch = () => {
    if (!searchValue.trim()) return;
    const updated = [
      searchValue,
      ...recentSearches.filter((s) => s !== searchValue),
    ].slice(0, 5);
    setRecentSearches(updated);
    localStorage.setItem("recentSearches", JSON.stringify(updated));
    setIsDropdownOpen(false);
    navigate(`/${lang}/search?q=${encodeURIComponent(searchValue)}`);
  };

  const formatPrice = (price: number) =>
    new Intl.NumberFormat("uz-UZ", {
      style: "currency",
      currency: "UZS",
    }).format(price);

  const highlightMatch = (text: string | undefined, query: string) => {
    if (!text) return null;
    if (!query) return text;
    const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const parts = text.split(new RegExp(`(${escapedQuery})`, "gi"));
    return parts.map((part, i) =>
      part.toLowerCase() === query.toLowerCase() ? (
        <span key={i} className="font-semibold text-blue-600">
          {part}
        </span>
      ) : (
        part
      )
    );
  };

  return (
    <div className="relative w-full max-w-md mx-auto z-12">
      <div className="relative flex items-center">
        <input
          type="text"
          placeholder={t("search.placeholder")}
          className="w-full border border-gray-300 rounded-full px-5 py-3 pr-16 text-sm focus:outline-none focus:ring-1 focus:ring-black/50 transition"
          value={searchValue}
          onChange={(e) => {
            setSearchValue(e.target.value);
            setIsDropdownOpen(true);
          }}
          onFocus={() => setIsDropdownOpen(true)}
          onBlur={() => setTimeout(() => setIsDropdownOpen(false), 200)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              handleSearch();
              e.currentTarget.blur();
            }
          }}
        />

        {searchValue && (
          <FiX
            onClick={() => setSearchValue("")}
            className="absolute right-10 text-gray-500 hover:text-gray-700 cursor-pointer"
          />
        )}
        <FiSearch
          onClick={handleSearch}
          className="absolute right-3 text-gray-500 hover:text-gray-700 cursor-pointer"
        />
      </div>

      <AnimatePresence>
        {isDropdownOpen && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
            className="absolute w-full bg-white border border-gray-200 rounded-lg shadow-lg mt-2 max-h-96 overflow-y-auto z-10"
          >
            {isLoading ? (
              <div className="p-4 text-center text-gray-500">
                {t("search.loading")}
              </div>
            ) : products.length === 0 &&
              categories.length === 0 &&
              stores.length === 0 &&
              searchValue.trim() ? (
              <div className="p-4 text-center text-gray-500">
                {t("search.noResults") || "Ma’lumot topilmadi"}
              </div>
            ) : (
              <>
                {/* 🔍 Recent searches */}
                {!searchValue && recentSearches.length > 0 && (
                  <div className="p-2 border-b border-gray-200">
                    <h3 className="text-sm font-semibold text-gray-700 px-2">
                      {t("search.recentSearches")}
                    </h3>
                    {recentSearches.map((s, i) => (
                      <div
                        key={i}
                        className="px-4 py-2 hover:bg-gray-100 flex items-center justify-between text-sm text-gray-900 cursor-pointer"
                        onClick={() => {
                          setIsDropdownOpen(false);
                          navigate(`/${lang}/search?q=${s}`);
                        }}
                      >
                        <div className="flex items-center gap-2">
                          <FiSearch className="w-4 h-4 text-gray-500" /> {s}
                        </div>
                        <FiX
                          className="w-4 h-4 text-gray-400 hover:text-red-500"
                          onClick={(e) => {
                            e.stopPropagation();
                            const updated = recentSearches.filter(
                              (x) => x !== s
                            );
                            setRecentSearches(updated);
                            localStorage.setItem(
                              "recentSearches",
                              JSON.stringify(updated)
                            );
                          }}
                        />
                      </div>
                    ))}
                  </div>
                )}

                {/* 🛒 Products */}
                {products.length > 0 && (
                  <div className="p-2 border-b border-gray-200">
                    <h3 className="text-sm font-semibold text-gray-700 px-2">
                      {t("search.products")}
                    </h3>
                    {products.map((p) => (
                      <div
                        key={p.id}
                        onClick={() => {
                          setIsDropdownOpen(false);
                          navigate(`/${lang}/product/${p.id}`);
                        }}
                        className="px-4 py-2 hover:bg-gray-100 flex gap-3 items-center cursor-pointer text-sm text-gray-900"
                      >
                        <img
                          src={p.images?.[0] || "https://placehold.co/40x40"}
                          alt={p.title}
                          className="w-10 h-10 object-cover rounded"
                        />
                        <div>
                          <div>{highlightMatch(p.title, searchValue)}</div>
                          <div className="text-xs text-gray-500">
                            {formatPrice(p.price)}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* 🗂 Categories */}
                {categories.length > 0 && (
                  <div className="p-2 border-b border-gray-200">
                    <h3 className="text-sm font-semibold text-gray-700 px-2">
                      {t("search.categories")}
                    </h3>
                    {categories.map((c) => (
                      <div
                        key={c.id}
                        onClick={() => {
                          setIsDropdownOpen(false);
                          navigate(`/${lang}/category/${c.id}`);
                        }}
                        className="px-4 py-2 hover:bg-gray-100 flex gap-3 items-center cursor-pointer text-sm text-gray-900"
                      >
                        <img
                          src={
                            Array.isArray(c.image)
                              ? c.image[0]
                              : c.image || "https://placehold.co/40x40?text=CAT"
                          }
                          alt={c.name}
                          className="w-10 h-10 object-cover rounded"
                        />
                        <p>{highlightMatch(c.name, searchValue)}</p>
                      </div>
                    ))}
                  </div>
                )}

                {/* 🏬 Stores */}
                {stores.length > 0 && (
                  <div className="p-2">
                    <h3 className="text-sm font-semibold text-gray-700 px-2">
                      {t("search.storesSuggestion")}
                    </h3>
                    {stores.map((s) => (
                      <div
                        key={s.id}
                        onClick={() => {
                          setIsDropdownOpen(false);
                          navigate(`/${lang}/store/${s.store_address}`);
                        }}
                        className="px-4 py-2 hover:bg-gray-100 flex gap-3 items-center cursor-pointer text-sm text-gray-900"
                      >
                        <img
                          src={s.logo || "https://placehold.co/40x40"}
                          alt={s.name}
                          className="w-10 h-10 object-cover rounded"
                        />
                        <div>
                          {highlightMatch(s.name, searchValue)}
                          <div className="text-xs text-gray-500">
                            {s.address}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default memo(SearchInput);
